// Claim review actions on SingleClaim.vue (backend: ClaimReviewController):
// - tick several services/drugs and reject them in one go (POST claims/{id}/services/reject);
//   ticks survive reloads of the claim, so earlier selections aren't lost;
// - send the claim back to the facility with a note (POST claims/{id}/send-back, status 4) and the
//   facility's Resubmit (POST claims/{id}/resubmit, back to pending);
// - the 10% drug deduction for vetters and verifiers, one line per claim that is updated rather than
//   duplicated (POST claims/{id}/drug-deduction).
//
// Expects `user`, `claimdetails`, `singleclaim`, `comments`, `remarks`, `claimStatus`, `isFacilityUser`
// and `getClaim()` on the component.
import { claimStage, CLAIM_STAGE } from "@/utils/claimRoles";

const DRUG_DEDUCTION_SERVICE_ID = 2019;

export default {
  data() {
    return {
      selectedLines: [],
      bulkRemark: "",
      bulkComment: "",
      sendBackOpen: false,
      sendBackReason: "",
      reviewBusy: false,
    };
  },

  computed: {
    reviewStage() {
      return claimStage(this.user);
    },

    isAgencyReviewer() {
      return this.reviewStage >= CLAIM_STAGE.VET;
    },

    // Vetting and verifying reset a claim to pending, so neither is offered once it has moved on.
    claimOpenForReview() {
      const c = this.claimdetails || {};
      return !this.claimLocked && ![2, 3, 4].includes(Number(c.status));
    },

    canVetLines() {
      return this.isAgencyReviewer && !this.claimLocked;
    },

    canVerifyLines() {
      return this.reviewStage >= CLAIM_STAGE.VERIFY && !this.claimLocked;
    },

    canVetClaim() {
      const c = this.claimdetails || {};
      return this.isAgencyReviewer && this.claimOpenForReview && c.checked_by_id == null && c.verified_by_id == null;
    },

    canVerifyClaim() {
      const c = this.claimdetails || {};
      return this.reviewStage >= CLAIM_STAGE.VERIFY && this.claimOpenForReview && c.verified_by_id == null;
    },

    canApproveClaim() {
      const c = this.claimdetails || {};
      const forwardedOrOpen = c.status === null || c.status === undefined || Number(c.status) === 3;
      return this.reviewStage === CLAIM_STAGE.APPROVE && !this.claimLocked && forwardedOrOpen && c.verified_by_id != null;
    },

    claimLocked() {
      const c = this.claimdetails || {};
      return Number(c.status) === 1 || !!(c.paymentorders && c.paymentorders.length);
    },

    canReviewLines() {
      return this.isAgencyReviewer && !this.claimLocked && !!this.singleclaim && Array.isArray(this.singleclaim.services);
    },

    canSendBack() {
      const c = this.claimdetails || {};
      return this.isAgencyReviewer && !this.claimLocked && c.id && ![2, 4].includes(Number(c.status));
    },

    canDeductDrugs() {
      return this.canVetLines;
    },

    drugDeductionLine() {
      const lines = (this.singleclaim && this.singleclaim.services) || [];
      return lines.find((l) => Number(l.services_id) === DRUG_DEDUCTION_SERVICE_ID && !l.drugs_id) || null;
    },

    allLinesSelected() {
      const lines = (this.singleclaim && this.singleclaim.services) || [];
      return lines.length > 0 && lines.every((l) => this.selectedLines.includes(l.id));
    },

    selectedLineNames() {
      const lines = (this.singleclaim && this.singleclaim.services) || [];
      return lines.filter((l) => this.selectedLines.includes(l.id))
        .map((l) => (l.drug ? l.drug.drug_name : l.service ? l.service.description : "Item " + l.id));
    },

    // The note left when the claim was last sent back (comments are newest first).
    sendBackNote() {
      const list = Array.isArray(this.comments) ? this.comments : [];
      return list.find((c) => /^Sent back for correction:/.test(c.body || "")) || null;
    },
  },

  watch: {
    // Keep ticks across reloads, dropping items that are no longer on the claim.
    singleclaim(claim) {
      const ids = ((claim && claim.services) || []).map((l) => l.id);
      this.selectedLines = this.selectedLines.filter((id) => ids.includes(id));
    },
    "$route.params.id"() {
      this.selectedLines = [];
    },
  },

  methods: {
    approveClaim() {
      if (!confirm("Approve this claim for payment?")) return;
      this.axios
        .post(`/api/v1/auth/acceptClaim/${this.$route.params.id}`)
        .then(() => {
          this.getClaim();
          this.$toasted.info("Claim approved.", { position: "top-center", duration: 3000 });
        })
        .catch(this.reviewError);
    },

    reviewError(error, fallback = "That action failed.") {
      const d = error && error.response && error.response.data;
      const first = d && d.errors ? Object.values(d.errors)[0] : null;
      this.$toasted.error((d && (d.message || d.Message)) || (Array.isArray(first) ? first[0] : first) || fallback, { position: "top-center", duration: 5000 });
    },

    toggleAllLines(checked) {
      const lines = (this.singleclaim && this.singleclaim.services) || [];
      this.selectedLines = checked ? lines.map((l) => l.id) : [];
    },

    rejectSelectedLines() {
      if (!this.selectedLines.length) return;
      if (!this.bulkRemark) {
        this.$toasted.error("Choose a rejection reason first.", { position: "top-center", duration: 3000 });
        return;
      }
      if (!confirm(`Reject ${this.selectedLines.length} item(s)? Their vetted and verified amounts will be set to 0.`)) return;
      this.reviewBusy = true;
      this.axios
        .post(`/api/v1/auth/claims/${this.$route.params.id}/services/reject`, {
          ids: this.selectedLines,
          remark_id: this.bulkRemark,
          comment: this.bulkComment || undefined,
        })
        .then((res) => {
          this.$toasted.success(res.data.message || "Items rejected.", { position: "top-center", duration: 3000 });
          this.selectedLines = [];
          this.bulkRemark = "";
          this.bulkComment = "";
          this.getClaim();
        })
        .catch((e) => this.reviewError(e, "Could not reject the items."))
        .finally(() => { this.reviewBusy = false; });
    },

    openSendBack() {
      this.sendBackReason = "";
      this.sendBackOpen = true;
    },

    sendBackClaim() {
      this.reviewBusy = true;
      this.axios
        .post(`/api/v1/auth/claims/${this.$route.params.id}/send-back`, {
          reason: this.sendBackReason.trim(),
          service_ids: this.selectedLines,
        })
        .then((res) => {
          this.sendBackOpen = false;
          this.selectedLines = [];
          this.$toasted.success(res.data.message || "Claim sent back.", { position: "top-center", duration: 3000 });
          this.getClaim();
        })
        .catch((e) => this.reviewError(e, "Could not send the claim back."))
        .finally(() => { this.reviewBusy = false; });
    },

    resubmitClaim() {
      const note = prompt("Resubmit this claim for vetting. Add a note for the agency (optional):", "");
      if (note === null) return;
      this.reviewBusy = true;
      this.axios
        .post(`/api/v1/auth/claims/${this.$route.params.id}/resubmit`, { note: note || undefined })
        .then((res) => {
          this.$toasted.success(res.data.message || "Claim resubmitted.", { position: "top-center", duration: 3000 });
          this.getClaim();
        })
        .catch((e) => this.reviewError(e, "Could not resubmit the claim."))
        .finally(() => { this.reviewBusy = false; });
    },

    applyClaimDrugDeduction() {
      const again = !!this.drugDeductionLine;
      if (!confirm(again
        ? "Recalculate the 10% drug deduction from the current drug amounts? The existing deduction line is updated, not duplicated."
        : "Deduct 10% of the drug amounts from this claim?")) return;
      this.reviewBusy = true;
      this.axios
        .post(`/api/v1/auth/claims/${this.$route.params.id}/drug-deduction`)
        .then((res) => {
          this.$toasted.success(res.data.message || "Drug deduction applied.", { position: "top-center", duration: 4000 });
          this.getClaim();
        })
        .catch((e) => this.reviewError(e, "Could not apply the drug deduction."))
        .finally(() => { this.reviewBusy = false; });
    },
  },
};
